'use client';

/**
 * Reports Core Web Vitals (LCP, INP, CLS, TTFB, FCP) to Matomo as custom
 * events. Each metric is sent as: category="Web Vitals", action=<metric>,
 * name=<rating> (good|needs-improvement|poor), value=<rounded ms>.
 *
 * Also deepens RUM beyond the once-per-load CWV (almanak-76ev.13):
 *  - LongTask: main-thread blocks >100ms (the jank behind poor INP), bounded.
 *  - SoftNav: App Router client transitions, which CWV can't see (web-vitals
 *    reports at most once per hard page load). Per-soft-nav CWV isn't natively
 *    supported by web-vitals, so we surface transition frequency/destination
 *    plus the long-task jank that most affects soft-nav responsiveness.
 *
 * Lightweight: web-vitals is ~3KB gz, loaded lazily; all extra observers are
 * feature-detected and fail silently.
 *
 * Refs: almanak-y8eth, almanak-76ev.13
 */

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';

declare global {
  interface Window {
    _paq?: Array<Array<string | number | boolean>>;
  }
}

interface VitalMetric {
  name: 'CLS' | 'FCP' | 'INP' | 'LCP' | 'TTFB';
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
}

/** Push a Web Vitals trackEvent, guarding against Matomo not being loaded. */
function push(action: string, label: string, value: number): void {
  if (typeof window === 'undefined' || !window._paq) return;
  window._paq.push(['trackEvent', 'Web Vitals', action, label, value]);
}

function reportToMatomo(metric: VitalMetric): void {
  // CLS is unitless (layout-shift score × 1000 for stable integer values).
  // INP/LCP/FCP/TTFB are milliseconds (round to integer for cleaner segments).
  const value = metric.name === 'CLS' ? Math.round(metric.value * 1000) : Math.round(metric.value);
  push(metric.name, metric.rating, value);
}

// Only report main-thread blocks long enough to matter, and cap per page so a
// jank storm can't flood Matomo.
const LONG_TASK_MIN_MS = 100;
const MAX_LONG_TASKS = 10;

export function WebVitalsReporter() {
  const pathname = usePathname();
  const isFirstPath = useRef(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const wv = await import('web-vitals');
        if (cancelled) return;
        // Each handler fires at most once per page (CLS may update on hidden).
        wv.onCLS(reportToMatomo);
        wv.onFCP(reportToMatomo);
        wv.onINP(reportToMatomo);
        wv.onLCP(reportToMatomo);
        wv.onTTFB(reportToMatomo);
      } catch {
        // web-vitals load failed (e.g., adblock) — fail silently.
      }
    })();

    // Long tasks: the main-thread blocks that drive poor INP and jank. INP
    // summarizes interaction latency; this surfaces the blocking work behind it.
    let longTasks = 0;
    let observer: PerformanceObserver | undefined;
    try {
      const supported =
        typeof PerformanceObserver !== 'undefined' &&
        PerformanceObserver.supportedEntryTypes?.includes('longtask');
      if (supported) {
        observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.duration < LONG_TASK_MIN_MS || longTasks >= MAX_LONG_TASKS) continue;
            longTasks++;
            push('LongTask', 'main-thread', Math.round(entry.duration));
          }
        });
        observer.observe({ type: 'longtask', buffered: true });
      }
    } catch {
      // longtask entry type unsupported — fail silently.
    }

    return () => {
      cancelled = true;
      observer?.disconnect();
    };
  }, []);

  // Soft navigations (App Router client transitions). Skipped on the initial
  // mount (that's the hard load the CWV handlers already cover).
  useEffect(() => {
    if (isFirstPath.current) {
      isFirstPath.current = false;
      return;
    }
    push('SoftNav', pathname || '/', 1);
  }, [pathname]);

  return null;
}
